import React from 'react';
import domtoimage from 'dom-to-image';
import FileSaver from 'file-saver';
import { css } from 'emotion';
import { connect} from 'react-redux';
import { graphql, withApollo } from 'react-apollo';

import { classes as cn } from 'shared/styles';
import { MOA_COLOR } from 'shared/constants/colors';

import {
  noop,
  compose,
  isEmptyArray,
  extractFirstErrorMessage,
  getFirstQueryNameOf,
  appStateStore,
  oauthStore,
  omitDeep,
} from 'shared/helpers';
import { gqlOauthRequestTwitter } from 'shared/gql/auth';
import {
  gqlTweetWithMedia,  // XXX: Temp gql mutation for preview launch
} from 'shared/gql/content';
import {
  EXPORT_PREVIEW_SIZE_REF,
  IPHONE_X_LOGICAL_WIDTH,
  EXPORT_PREVIEW_ROOT_ID,
} from 'shared/constants/web';
import { mixClasses as cx } from 'helpers';

import {
  RectButton,
  SvgIcon,
  GhostButton,
  FontIcon,
  Textarea,
  Image,
  DoubleBouncer,
  Link,
} from 'components/elems';
import { withNotify } from 'components/hocs';
import { note } from 'presets';
import { EscCloseBtn } from './EscCloseBtn'


// Quality of image drawn and exported by domtoimage isn't
// the greatest at 100% scale, so blow it up a bit at export.
const EXPORT_PREVIEW_SCALE_FACTOR = 3;

// note: t.co link is counted as 18 chars worth?
const CAPTION_CHAR_LIMIT = 120;


const LabelSm = ({ children }) => (
  <div
    className="moa-ff-sans ttu silver pv1"
    style={{ fontSize: '11px' }}
  >
    {children}
  </div>
);


const ConnectAcctBtn = ({ icon = null, color, onClick, children }) => (
  <div
    className={cx(
      css({
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0, right: 0, bottom: 0, left: 0,
          background: 'currentColor',
          opacity: 0,
          borderRadius: '.25rem',  // br2
          transition: 'opacity 200ms',
        },
        '&:hover:before': {
          opacity: 0.1,
        },
      }),
      "inline-flex items-center relative",
      "moa-ff-sans f6 fw5 ph2 pointer",
      "br2 ba bw1"
    )}
    style={{
      height: '40px', color,
      borderColor: 'currentColor',
    }}
    onClick={onClick}
  >
    {icon && (
      <span className="flex mr1">{icon}</span>
    )}

    {children}
  </div>
);



const twitterIcon = <SvgIcon.fa.Twitter size={24} />;
const ConnectTwitterBtn = (props) => (
  <ConnectAcctBtn icon={twitterIcon} color="#1DA1F2" {...props}>
    Connect Twitter
  </ConnectAcctBtn>
);


const renderTinyIcon = (platform) => {
  const style = {
    position: 'absolute',
    background: '#FFF',
    borderRadius: '4px',
    right: '-6px',
    bottom: '-6px',
  };

  if (platform === 'twitter') {
    return (
      <SvgIcon.fa.TwitterSquare
        size={16}
        style={{ color: "#1DA1F2", ...style }}
      />
    );
  }

  // XXX: Implement for other platforms!!
  return null;
};


const ConnectedAcctTab = ({
  username,
  platform,
  profile_image_url
}) => {
  return (
    <div
      className="inline-flex items-start ph1 bb bw1"
      style={{
        height: '38px',
        borderColor: MOA_COLOR.pancake.puertoRico,
      }}
    >
      <div className="flex relative">
        <Image
          className="br-100"
          width={24}
          src={profile_image_url}
        />
        {renderTinyIcon(platform)}
      </div>

      <div
        className="moa-ff-sans fw3 f7 gray ml2 truncate"
        style={{
          maxWidth: '88px',  // for ellipsis
        }}
      >
        @{username}
      </div>
    </div>
  );
};



const PanelFrame = ({ style = {}, children }) => (
  <div
    className={cx(
      "flex flex-column justify-between ph3",
      "moa-ff-sans black-70 lh-copy bg-white",
    )}
    style={{ ...style }}
  >
    {children}
  </div>
);



function mapStateToProps(state) {
  return {
    viewState: state.view,
    draftState: state.draft,
  };
};




/*
 * StageContentPanel
 */
class StageContentPanel extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isSavingExport: false,
      isPostingContent: false,

      captionValue: 'Written with #Moagraph.com',

      connectedAccounts: Object.values(oauthStore.get()),
      committedContents: [],
    };

    this.requestOAuthTwitter = this.requestOAuthTwitter.bind(this);
    this.onChangeCaptionValue = this.onChangeCaptionValue.bind(this);
    this.publishContent = this.publishContent.bind(this);
  }

  renderExportPreviewScaled = (targetSize = EXPORT_PREVIEW_SIZE_REF) => {
    const rootId = EXPORT_PREVIEW_ROOT_ID;
    const targetScale = targetSize / EXPORT_PREVIEW_SIZE_REF;

    const style = {
      transform: `scale(${targetScale})`,
      transformOrigin: 'top left',
      height: `${targetSize}px`,
      width: `${targetSize}px`,
    };

    return this.props.renderExportPreview({ rootId, style });
  }

  exportPreviewConfig = (node, scaleFactor = EXPORT_PREVIEW_SCALE_FACTOR) => {
    const config = {
      style: {
        transform: `scale(${scaleFactor})`,
        transformOrigin: 'top left',
      },
      width: node.offsetWidth * scaleFactor,
      height: node.offsetHeight * scaleFactor,
      cacheBust: true,
    }

    return config;
  }

  exportPreviewBlob = (node) => {
    const config = this.exportPreviewConfig(node);
    return domtoimage.toBlob(node, config)
  }

  convertBlobToFile = (blob, filename = "via-moagraph.png") => {
    return new File([blob], filename, {type: blob.type});
  }

  downloadPreviewImage = () => {
    const node = document.getElementById(EXPORT_PREVIEW_ROOT_ID);
    // TODO: Indicate error in UI, report error to monitoring services etc.
    // Also add catch clause below?
    if (!node) return;

    this.setState({ isSavingExport: true });

    this
      .exportPreviewBlob(node)
      .then(blob => {
        FileSaver.saveAs(blob, 'via-moagraph.png');
        this.setState({ isSavingExport: false });
      });
  }

  // TODO: Prob syndication section should be its own component w/
  // these associated functions in the future..
  requestOAuthTwitter = () => {
    const opts = { query: gqlOauthRequestTwitter };

    this.props.client
      .query(opts)
      .then(resp => {
        const key = getFirstQueryNameOf(opts.query);
        const { [key]: payload } = resp.data;

        if (isEmptyArray(payload.errors)) {
          const metadata = JSON.parse(payload.metadata);

          // Save current view and draft state before redirecting.
          appStateStore.set({
            view: this.props.viewState.toJS(),
            draft: this.props.draftState.toJS(),
          });

          return window.open(metadata.authenticate_url, '_self');
        }

        const message = extractFirstErrorMessage(payload.errors);
        this.props.notifyError(message);

      }).catch(err => {
        const message = "Oops, something went wrong. Try again later?";
        this.props.notifyError(message);
      });
  }

  onChangeCaptionValue = (event) => {
    const captionValue = event.target.value;
    this.setState({ captionValue });
  }

  validateStagedData = () => {
    const { hasValidContent } = this.props;
    const { connectedAccounts } = this.state;
    const hasConnectedAccounts = connectedAccounts.length > 0;

    const captionLength = this.state.captionValue.length;
    const hasValidCaptionLength = captionLength < CAPTION_CHAR_LIMIT;

    return (
      hasValidContent
      && hasConnectedAccounts
      && hasValidCaptionLength
    );
  }

  publishContent = () => {
    // XXX: Later when ready to post within mosgraph,
    // parent composer component will need to pass the content state.
    // TODO: Indicate error in UI if doesn't exist?
    const node = document.getElementById(EXPORT_PREVIEW_ROOT_ID);
    if (!node) return;

    this.setState({ isPostingContent: true });

    // XXX: For now.. post to twitter only.
    const matchTwitterAcct = acct => acct.platform === 'twitter';
    const { captionValue, connectedAccounts } = this.state;

    this.exportPreviewBlob(node)
      .then(blob => {
        const twitterPostInput = {
          caption: captionValue,
          oauthUser: connectedAccounts.find(matchTwitterAcct),
          media: this.convertBlobToFile(blob),
        };

        return this.props.commitContent(twitterPostInput);
      })
      .then(resp => {
        const key = getFirstQueryNameOf(gqlTweetWithMedia);
        const { [key]: payload } = resp.data;
        const message = extractFirstErrorMessage(payload.errors);
        this.props.notifyError(message);

        // XXX: Only twitter for now..
        const tweetData = omitDeep(payload.tweetData);
        this.setState({
          committedContents: [tweetData],
        });

        this.setState({ isPostingContent: false });
      })
      .catch(error => {
        // Todo: make these DRY.
        const message = "Oops, something went wrong. Try again later?";
        this.props.notifyError(message);
        this.setState({ isPostingContent: false });
      });;
  }

  renderSyndChannels = () => {
    const { connectedAccounts } = this.state;
    if (connectedAccounts.length === 0) {
      return (
        <ConnectTwitterBtn onClick={this.requestOAuthTwitter} />
      );
    }

    const orderByPlatformName = (a, b) => {
      if (a.platform < b.platform) return -1;
      if (a.platform > b.platform) return 1;
      return 0;
    };

    return connectedAccounts
      .sort(orderByPlatformName)
      .map(acct => (
        <ConnectedAcctTab
          key={"connected-acct-tab-" + acct.platform}
          {...acct}
        />
      ));
  }

  renderCommitResult = (committedContents) => {
    // NOTE(thx): Sample data
    // committedContents = [
    //   {
    //     tweetId: 948331534897045506,
    //     tweetUrl: "https://twitter.com/moagraph/status/948331534897045506",
    //   },
    // ];

    // XXX: Should be only one, for twitter.. lol
    const tweetData = committedContents[0];

    return (
      <PanelFrame>
        <div className="flex justify-between items-center mv2">
          <EscCloseBtn onClick={this.props.onCancel} />
        </div>
        <div className="f4 fw5 mb3">
          Success!
          <span className="ml2" role="img" aria-label="headsup">🎉</span>
        </div>
        {note.info(
          <Link href={tweetData.tweetUrl} className="fw4">
            {tweetData.tweetUrl}
          </Link>,
          { title: "Twitter:" }
        )}
        <div className='mt4 f6'>
          ---
        </div>
        <div className="f6 mb3 lh-copy">
          <div className="mb2">
            <b>Thanks for using Moagraph.</b>
          </div>
          For any questions or feedback, feel free to send them <Link href="mailto:hello@moagraph.com" className="fw4">here</Link>.&nbsp;
          You can also <Link href="https://tinyletter.com/moagraph" className="fw4">subscribe</Link> to
          the mailing list to get updates on the project.
        </div>
        <div className="flex justify-end mt3">
          <button
            onClick={this.props.onCancel}
            className={cx("-button glow o-60 mid-gray f6 mh3")}
          >
            Close
          </button>
        </div>
      </PanelFrame>
    );
  }

  render() {
    const { style = {}, onCancel = noop } = this.props;

    const { committedContents } = this.state;
    if (committedContents.length) {
      return this.renderCommitResult(committedContents);
    }

    // margin + padding on sides to inner content of modal
    const sideSpacing = 16*2;
    const fittedSize = (
      this.props.renderedWidth || (IPHONE_X_LOGICAL_WIDTH - sideSpacing)
    ) - sideSpacing;

    const captionLength = this.state.captionValue.length;
    const hasValidCaptionLength = captionLength < CAPTION_CHAR_LIMIT;

    const postBtnIcon = (this.state.isPostingContent
      ? <DoubleBouncer />
      : <SvgIcon.md.Send />
    );

    return (
      <PanelFrame style={style}>
        <div>
          <div className="flex justify-between items-center mv2">
            <EscCloseBtn onClick={this.props.onCancel} />
          </div>

          <div className="mb3">
            <div className="f4 fw5 mb4 dark-gray">
              Ready to publish?
            </div>

            <div className="f6 mb3">
              Moagraph is currently in Alpha.
              During this time, you can connect your social account
              on the supported platforms below to post directly, or
              save to export your writing into an image and post manually.
            </div>
          </div>

          <div className="flex flex-wrap justify-between mb3">
            <div className="w-100 mb3">
              {this.renderSyndChannels()}
            </div>

            <div className="w-100">
              <LabelSm>Caption</LabelSm>
              <Textarea
                value={this.state.captionValue}
                onChange={this.onChangeCaptionValue}
                className="w-100"
              />
              <div
                className={cx(
                  "tr fw3",
                  hasValidCaptionLength ? 'moon-gray' : 'red',
                )}
                style={{ fontSize: '11px', }}
              >
                {captionLength} / {CAPTION_CHAR_LIMIT}
              </div>
            </div>

            <div>
              <LabelSm>Export Preview</LabelSm>

              <div
                className="relative"
                style={{ width: fittedSize, height: fittedSize }}
              >
                <div className="absolute top-0">
                  {this.renderExportPreviewScaled(fittedSize)}
                </div>
              </div>

              <div className="flex justify-end mt2">
                <GhostButton
                  onClick={this.downloadPreviewImage}
                  className="f7 pv1 ph2 br2"
                >
                  {this.state.isSavingExport ? 'Saving..' : 'Save'}
                  <FontIcon
                    type="download"
                    style={{ fontSize: '12px' }}
                    className="ml1"
                  />
                </GhostButton>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between mt3 pv3">
          <button
            onClick={onCancel}
            className={cx("-button glow o-60 mid-gray f6 mh2")}
          >
            Close
          </button>
          <RectButton
            className={cn.boxShadowRaisedButton}
            label="POST"
            icon={postBtnIcon}
            isEnabled={this.validateStagedData()}
            isActivated={this.state.isPostingContent}
            onClick={this.publishContent}
            style={{ fontSize: '12px' }}
            theme="primary"
          />
        </div>
      </PanelFrame>
    );
  }
}

          // <div>
          //   <input type="checkbox" />
          //   <label class="inline">
          //     Subscribe to project updates.
          //   </label>
          // </div>


const gqlTweetWithMediaCfg = {
  props: ({ mutate }) => ({
    // XXX: For now, only twitter.. :(
    commitContent: (twitterPostInput) => mutate({
      variables: {input: twitterPostInput},
    }),
  }),
};


export default compose(
  withApollo,
  withNotify,
  graphql(gqlTweetWithMedia, gqlTweetWithMediaCfg),
  connect(mapStateToProps),
)(StageContentPanel);



// WEBPACK FOOTER //
// ./src/components/modules/common/StageContentPanel.js